/**
 * routes/Trades — the closed-trade memory (TF.4). Reads /api/trades (the trade_memories
 * learning rows) and renders them as a sortable, filterable table: time, symbol, setup,
 * planned vs realized R, result, the MAE/MFE excursions, leverage and a per-trade R-journey
 * sparkline.
 *
 * The table shows the closed-trade learning memory plus durable exchange execution
 * facts (side, quantity, entry and exit) when known.
 */
import { useMemo, useState } from 'react';

import { api } from '../api/client';
import type { TradeRecord, TradeResult, TradesResponse } from '../api/types';
import { usePoll } from '../api/usePoll';
import { Card } from '../components/Card';
import { Sparkline } from '../components/charts/Sparkline';
import { ago, num } from '../lib/format';

type ResultFilter = 'ALL' | TradeResult;

const RESULT_FILTERS: readonly { key: ResultFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'WIN', label: 'Wins' },
  { key: 'LOSS', label: 'Losses' },
  { key: 'BREAKEVEN', label: 'B/E' },
];

const RESULT_BADGE: Record<TradeResult, string> = {
  WIN: 'badge--profit',
  LOSS: 'badge--loss',
  BREAKEVEN: 'badge--neutral',
};
const RESULT_SHORT: Record<TradeResult, string> = { WIN: 'W', LOSS: 'L', BREAKEVEN: 'BE' };

type SortKey = 'time' | 'symbol' | 'entryPrice' | 'exitPrice' | 'rrRealized' | 'netPnlUsd' | 'netR' | 'leverage';
type SortDir = 'asc' | 'desc';

/** Sort accessor per column. `time` sorts by id (monotonic, newest-first by default). */
function sortValue(t: TradeRecord, key: SortKey): number | string {
  switch (key) {
    case 'time': return t.id;
    case 'symbol': return t.symbol;
    case 'entryPrice': return t.entryPrice ?? -Infinity;
    case 'exitPrice': return t.exitPrice ?? -Infinity;
    case 'rrRealized': return t.rrRealized ?? -Infinity;
    case 'netPnlUsd': return t.netPnlUsd ?? -Infinity;
    case 'netR': return t.netR ?? -Infinity;
    case 'leverage': return t.leverage;
  }
}

/** Signed R, e.g. +1.60R / −1.00R (real minus glyph, tabular). */
function rMult(v: number | null): string {
  if (v == null) return '—';
  const s = Math.abs(v).toFixed(2);
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${s}R`;
}
function rClass(v: number | null): string {
  return v == null ? '' : v > 0 ? 'profit' : v < 0 ? 'loss' : '';
}
function price(v: number | null): string {
  return v == null ? '-' : num(v, v >= 1000 ? 2 : 4);
}
function qty(v: number | null): string {
  return v == null ? '-' : num(v, 4);
}

interface HeaderCell {
  readonly key: SortKey;
  readonly label: string;
  readonly align?: 'right';
}
const SORTABLE: readonly HeaderCell[] = [
  { key: 'time', label: 'Time' },
  { key: 'symbol', label: 'Symbol' },
  { key: 'entryPrice', label: 'Entry', align: 'right' },
  { key: 'exitPrice', label: 'Exit', align: 'right' },
  { key: 'rrRealized', label: 'R', align: 'right' },
  { key: 'netPnlUsd', label: 'Net', align: 'right' },
  { key: 'netR', label: 'Net R', align: 'right' },
  { key: 'leverage', label: 'Lev', align: 'right' },
];

export function Trades(): JSX.Element {
  const trades = usePoll<TradesResponse>((signal) => api.trades(signal, 200), { intervalMs: 30_000 });

  const [result, setResult] = useState<ResultFilter>('ALL');
  const [symbol, setSymbol] = useState<string>('ALL');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'time', dir: 'desc' });

  const rows = trades.data?.trades ?? [];
  const symbols = useMemo(() => Array.from(new Set(rows.map((t) => t.symbol))).sort(), [rows]);

  const view = useMemo(() => {
    const filtered = rows.filter(
      (t) => (result === 'ALL' || t.result === result) && (symbol === 'ALL' || t.symbol === symbol),
    );
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, result, symbol, sort]);

  const onSort = (key: SortKey): void =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const loading = trades.loading && !trades.data;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Trades</h1>
        <span className="page-head__sub">
          {trades.updatedAt ? `updated ${ago(trades.updatedAt)}` : 'loading…'}
        </span>
      </div>

      {trades.error && !trades.data && (
        <div className="banner-error section" role="alert">
          Trade history unavailable: {trades.error}
        </div>
      )}

      <Card title="Closed trades" hint={rows.length ? `${view.length} of ${rows.length}` : undefined} flush>
        <div className="filters" role="group" aria-label="Filter trades">
          <div className="filters__group">
            <span className="filters__label">Result</span>
            <div className="seg" role="group" aria-label="Filter by result">
              {RESULT_FILTERS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className={`seg__btn${result === r.key ? ' is-active' : ''}`}
                  aria-pressed={result === r.key}
                  onClick={() => setResult(r.key)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="filters__group">
            <span className="filters__label">Symbol</span>
            <div className="seg" role="group" aria-label="Filter by symbol">
              <button
                type="button"
                className={`seg__btn${symbol === 'ALL' ? ' is-active' : ''}`}
                aria-pressed={symbol === 'ALL'}
                onClick={() => setSymbol('ALL')}
              >
                All
              </button>
              {symbols.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`seg__btn${symbol === s ? ' is-active' : ''}`}
                  aria-pressed={symbol === s}
                  onClick={() => setSymbol(s)}
                >
                  {s.replace('USDT', '')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="state"><span className="spinner" aria-hidden /><span>Loading trades…</span></div>
        ) : (
          <div className="table-wrap">
            <table className="data-table trades-table">
              <thead>
                <tr>
                  {SORTABLE.map((h) => {
                    const active = sort.key === h.key;
                    return (
                      <th
                        key={h.key}
                        className={h.align === 'right' ? 'align-right' : undefined}
                        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      >
                        <button type="button" className={`th-sort${active ? ' is-active' : ''}`} onClick={() => onSort(h.key)}>
                          {h.label}
                          <span className="th-sort__arrow" aria-hidden>{active ? (sort.dir === 'asc' ? '↑' : '↓') : ''}</span>
                        </button>
                      </th>
                    );
                  })}
                  <th>Side</th>
                  <th className="align-right">Qty</th>
                  <th>Setup</th>
                  <th>Result</th>
                  <th className="align-right">Journey</th>
                </tr>
              </thead>
              <tbody>
                {view.length === 0 ? (
                  <tr>
                    <td className="data-table__empty" colSpan={SORTABLE.length + 5}>
                      {rows.length === 0 ? (
                        <span className="empty-note">
                          <strong>No closed trades yet.</strong>
                          <span className="muted"> The committee has not entered and closed a trade — this table fills in as trades complete.</span>
                        </span>
                      ) : (
                        'No trades match these filters.'
                      )}
                    </td>
                  </tr>
                ) : (
                  view.map((t) => (
                    <tr key={t.id}>
                      <td className="muted num nowrap">{ago(Date.parse(t.createdAt))}</td>
                      <td>
                        <strong>{t.symbol}</strong> <span className="muted">{t.timeframe}</span>
                      </td>
                      <td className="align-right num">{price(t.entryPrice)}</td>
                      <td className="align-right num">{price(t.exitPrice)}</td>
                      <td className={`align-right num ${rClass(t.rrRealized)}`}>{rMult(t.rrRealized)}</td>
                      <td className={`align-right num ${rClass(t.netPnlUsd)}`}>
                        {t.netPnlUsd == null ? '-' : `${t.netPnlUsd > 0 ? '+' : ''}$${num(t.netPnlUsd, 2)}`}
                      </td>
                      <td className={`align-right num ${rClass(t.netR)}`}>{rMult(t.netR)}</td>
                      <td className="align-right num">{t.leverage}×</td>
                      <td><span className="badge badge--neutral">{t.side ?? '-'}</span></td>
                      <td className="align-right num">{qty(t.quantity)}</td>
                      <td>
                        <span className="trade-setup">{t.setupType}</span>
                        {t.marketRegime && <span className="muted trade-regime"> · {t.marketRegime}</span>}
                      </td>
                      <td>
                        <span className={`badge ${RESULT_BADGE[t.result]}`}>{RESULT_SHORT[t.result]}</span>
                      </td>
                      <td className="trades-table__spark">
                        <Sparkline realized={t.rrRealized} mae={t.mae} mfe={t.mfe} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
